import { Account, Client, ID, TablesDB } from "appwrite";

// Hosted NetBase defaults. Used only when neither a runtime config (`window.__NETBASE_CONFIG__`)
// nor a build-time Vite env var is provided.
const HOSTED_DEFAULT_ENDPOINT = "https://api.netbaseapp.com/v1";
const HOSTED_DEFAULT_PROJECT_ID = "cisco-pm";
const HOSTED_DEFAULT_DATABASE_ID = "69cba4b700161614419f";
const DEFAULT_AUTH_MODE = "oidc";

const runtimeConfig =
  (typeof window !== "undefined" && window.__NETBASE_CONFIG__) || {};

function pickConfigValue(...candidates) {
  for (const value of candidates) {
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }
  return "";
}

export const APPWRITE_ENDPOINT =
  pickConfigValue(
    runtimeConfig.APPWRITE_ENDPOINT,
    import.meta.env.VITE_APPWRITE_ENDPOINT
  ) || HOSTED_DEFAULT_ENDPOINT;

export const APPWRITE_PROJECT_ID =
  pickConfigValue(
    runtimeConfig.APPWRITE_PROJECT_ID,
    import.meta.env.VITE_APPWRITE_PROJECT_ID
  ) || HOSTED_DEFAULT_PROJECT_ID;

export const AUTH_MODE =
  pickConfigValue(
    runtimeConfig.AUTH_MODE,
    import.meta.env.VITE_AUTH_MODE
  ) || DEFAULT_AUTH_MODE;

// Database id. Three-tier precedence mirrors the endpoint/project setup:
//   1. `window.__NETBASE_CONFIG__.APPWRITE_DATABASE_ID` — injected by the
//      self-hosted Docker image at container start.
//   2. `VITE_APPWRITE_DATABASE_ID` — build-time env for local dev / CI.
//   3. The hosted NetBase default (kept as a literal for back-compat).
export const APPWRITE_DATABASE_ID =
  pickConfigValue(
    runtimeConfig.APPWRITE_DATABASE_ID,
    import.meta.env.VITE_APPWRITE_DATABASE_ID
  ) || HOSTED_DEFAULT_DATABASE_ID;

const runtimeConfigPresent =
  typeof window !== "undefined" && Boolean(window.__NETBASE_CONFIG__);
const runtimeConfigEmpty =
  runtimeConfigPresent &&
  !(runtimeConfig.APPWRITE_ENDPOINT || "").trim() &&
  !(runtimeConfig.APPWRITE_PROJECT_ID || "").trim();

/**
 * True when a runtime-config script is loaded (typically the self-hosted
 * Docker image) but every endpoint value is blank. The UI shows a banner in
 * this state so operators don't silently end up talking to the hosted API.
 */
export const RUNTIME_CONFIG_MISCONFIGURED =
  runtimeConfigEmpty &&
  APPWRITE_ENDPOINT === HOSTED_DEFAULT_ENDPOINT &&
  APPWRITE_PROJECT_ID === HOSTED_DEFAULT_PROJECT_ID &&
  !import.meta.env.DEV;

if (RUNTIME_CONFIG_MISCONFIGURED) {
  console.warn(
    "[NetBase] Runtime config is present but APPWRITE_ENDPOINT / APPWRITE_PROJECT_ID are empty. " +
      "Falling back to the hosted NetBase API. If you are self-hosting, set APPWRITE_ENDPOINT " +
      "and APPWRITE_PROJECT_ID on your container."
  );
}

export const PROJECTS_TABLE_ID = "projects";
export const PROJECT_MEMBERS_TABLE_ID = "project_members";
export const PROJECT_DEVICES_TABLE_ID = "project_devices";
export const PROJECT_PAGES_TABLE_ID = "project_pages";
export const PROJECT_PRESENCE_TABLE_ID = "project_presence";
export const CONNECTORS_TABLE_ID = "connectors";
export const CONNECTOR_ENROLLMENTS_TABLE_ID = "connector_enrollments";
export const CONNECTOR_TARGETS_TABLE_ID = "connector_targets";
export const DEVICE_RUNTIME_TABLE_ID = "device_runtime";
export const DEVICE_UPTIME_HISTORY_TABLE_ID = "device_uptime_history";
export const DEVICE_INTERFACES_TABLE_ID = "device_interfaces";
export const DEVICE_CONFIGS_TABLE_ID = "device_configs";
export const DEPLOYMENT_JOBS_TABLE_ID = "deployment_jobs";
export const DEPLOYMENT_LOGS_TABLE_ID = "deployment_logs";
export const PROJECT_ACTIVITY_TABLE_ID = "project_activity";

export const client = new Client()
  .setEndpoint(APPWRITE_ENDPOINT)
  .setProject(APPWRITE_PROJECT_ID);

export { ID };
export const tablesDB = new TablesDB(client);
export const account = new Account(client);
